import React from 'react';
import { Modal } from '../ui/Modal';
import { AuditLog, AUDIT_ACTION_LABELS, AUDIT_ENTITY_LABELS } from '../../types/audit';
import { ArrowRight } from 'lucide-react';

interface AuditLogDetailProps {
  log: AuditLog | null;
  onClose: () => void;
}

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const DiffRow: React.FC<{ label: string; oldVal: any; newVal: any }> = ({ label, oldVal, newVal }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2 py-2 border-b border-slate-800/60 last:border-0">
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">{label}</div>
      <pre className="text-[11px] text-rose-300 whitespace-pre-wrap break-words font-mono bg-rose-500/5 rounded p-1.5">{formatValue(oldVal)}</pre>
    </div>
    <ArrowRight className="w-3.5 h-3.5 text-slate-600 mt-5 shrink-0" />
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">&nbsp;</div>
      <pre className="text-[11px] text-emerald-300 whitespace-pre-wrap break-words font-mono bg-emerald-500/5 rounded p-1.5">{formatValue(newVal)}</pre>
    </div>
  </div>
);

export const AuditLogDetail: React.FC<AuditLogDetailProps> = ({ log, onClose }) => {
  if (!log) return null;

  const keys = Array.from(new Set([
    ...Object.keys(log.old_values || {}),
    ...Object.keys(log.new_values || {}),
  ]));

  return (
    <Modal isOpen={!!log} onClose={onClose} title="Audit Log Detail">
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-slate-500">Log ID</div>
            <div className="text-slate-200 font-mono">{log.log_id}</div>
          </div>
          <div>
            <div className="text-slate-500">Timestamp</div>
            <div className="text-slate-200">{new Date(log.timestamp).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-slate-500">Action</div>
            <div className="text-slate-200">{AUDIT_ACTION_LABELS[log.action]}</div>
          </div>
          <div>
            <div className="text-slate-500">Entity</div>
            <div className="text-slate-200">{AUDIT_ENTITY_LABELS[log.entity_type]} {log.entity_id && <span className="font-mono text-slate-400">({log.entity_id})</span>}</div>
          </div>
          <div>
            <div className="text-slate-500">User</div>
            <div className="text-slate-200">{log.user_name || log.user_id || 'System'}</div>
          </div>
          <div>
            <div className="text-slate-500">IP Address</div>
            <div className="text-slate-200 font-mono">{log.ip_address || '—'}</div>
          </div>
        </div>

        {log.description && (
          <div className="text-xs">
            <div className="text-slate-500 mb-1">Description</div>
            <div className="text-slate-200 bg-slate-800/50 rounded-lg p-2.5">{log.description}</div>
          </div>
        )}

        {log.user_agent && (
          <div className="text-xs">
            <div className="text-slate-500 mb-1">User Agent</div>
            <div className="text-slate-400 break-words font-mono text-[10px]">{log.user_agent}</div>
          </div>
        )}

        {keys.length > 0 && (
          <div>
            <div className="text-xs text-slate-500 mb-2">Change Diff (Old → New)</div>
            <div className="bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-1">
              {keys.map(key => (
                <DiffRow
                  key={key}
                  label={key}
                  oldVal={log.old_values?.[key]}
                  newVal={log.new_values?.[key]}
                />
              ))}
            </div>
          </div>
        )}

        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <div className="text-xs">
            <div className="text-slate-500 mb-1">Metadata</div>
            <pre className="text-[11px] text-slate-300 bg-slate-900/60 border border-slate-800 rounded-lg p-2.5 overflow-x-auto">
              {JSON.stringify(log.metadata, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  );
};
